import { useState, useCallback } from "react";
import { X, Upload, Image as ImageIcon } from "react-feather";
import { storageService } from "../../services/storage.service";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { toast } from "react-hot-toast";
import { ImageCropModal } from "../ImageCropModal/ImageCropModal";
import { readFile } from "../../utils/canvasUtils";

interface ShowcaseImage {
  id: string;
  url: string;
  file?: File;
}

interface ShowcaseImagesManagerProps {
  images: ShowcaseImage[];
  onChange: (images: ShowcaseImage[]) => void;
  maxImages?: number;
}

export function ShowcaseImagesManager({
  images,
  onChange,
  maxImages = 5,
}: ShowcaseImagesManagerProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState<string>("");
  const [tempImageFile, setTempImageFile] = useState<File | null>(null);

  const handleFileUpload = useCallback(
    async (file: File) => {
      if (images.length >= maxImages) {
        toast.error(`Maximum ${maxImages} images allowed`);
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        toast.error("Image size should be less than 5MB");
        return;
      }

      try {
        // Read the file as a data URL for the cropper
        const imageDataUrl = await readFile(file);
        
        // Store the file and image source, then open the crop modal
        setTempImageFile(file);
        setTempImageSrc(imageDataUrl);
        setShowCropModal(true);
      } catch (error) {
        console.error('Error reading file:', error);
        toast.error('Failed to load image. Please try again.');
      }
    },
    [images, maxImages]
  );

  const handleCropComplete = useCallback(
    async (croppedFile: File) => {
      setIsUploading(true);
      try {
        const imageUrl = await storageService.uploadShowcaseImage(croppedFile);
        const newImage: ShowcaseImage = {
          id: `temp-${Date.now()}`,
          url: imageUrl,
          file: croppedFile,
        };

        onChange([...images, newImage]);
        toast.success("Image uploaded successfully");
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("Failed to upload image");
      } finally {
        setIsUploading(false);
        setShowCropModal(false);
        setTempImageSrc("");
        setTempImageFile(null);
      }
    },
    [images, onChange]
  );

  const handleCropCancel = useCallback(() => {
    setShowCropModal(false);
    setTempImageSrc("");
    setTempImageFile(null);
  }, []);

  const handleRemoveImage = useCallback(
    (imageId: string) => {
      onChange(images.filter((img) => img.id !== imageId));
    },
    [images, onChange]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileUpload(e.dataTransfer.files[0]);
      }
    },
    [handleFileUpload]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        handleFileUpload(e.target.files[0]);
      }
    },
    [handleFileUpload]
  );

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="w-5 h-5" />
          Showcase Images
        </CardTitle>
        <CardDescription>
          Upload up to {maxImages} images to showcase your brand. These will be
          displayed as a carousel on your brand details page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Area */}
        {images.length < maxImages && (
          <div
            className="border-2 border-dashed border-border rounded-lg p-6 text-center transition-colors hover:border-brand-500 hover:bg-brand-50"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center gap-2">
              <Upload className="h-8 w-8 text-muted-foreground" />
              <div className="text-sm text-muted-foreground">
                <input
                  type="file"
                  onChange={handleFileInput}
                  accept="image/*"
                  className="hidden"
                  id="showcase-upload"
                  disabled={isUploading}
                />
                <label
                  htmlFor="showcase-upload"
                  className="cursor-pointer text-brand-600 hover:text-brand-700 font-medium"
                >
                  {isUploading ? "Uploading..." : "Click to upload"}
                </label>
                <span className="mx-1">or drag and drop</span>
              </div>
              <p className="text-xs text-muted-foreground">PNG, JPG, GIF up to 5MB</p>
            </div>
          </div>
        )}

        {/* Images Grid */}
        {images.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {images.map((image, index) => (
              <div key={image.id} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden border border-border">
                  <img
                    src={image.url}
                    alt={`Showcase ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  onClick={() => handleRemoveImage(image.id)}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Image Count */}
        <div className="text-sm text-muted-foreground text-center">
          {images.length} of {maxImages} images uploaded
        </div>
      </CardContent>

      {/* Crop Modal */}
      {showCropModal && tempImageSrc && tempImageFile && (
        <ImageCropModal
          imageSrc={tempImageSrc}
          onClose={handleCropCancel}
          onCropComplete={handleCropComplete}
          originalFileName={tempImageFile.name}
          aspectRatio={1}
          title="Crop Showcase Image"
          description="Adjust the zoom and position to crop your image (1:1 square aspect ratio)"
        />
      )}
    </Card>
  );
}
